import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, Grid } from '@mui/material';
import { useMachine } from '@xstate/react';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Confirmation } from '../../molecules/create-destination/confirmation';
import { DestinationInfo } from '../../molecules/create-destination/destination-info';
import { DirectionTips } from '../../molecules/create-destination/direction-tips';
import { Hosts } from '../../molecules/create-destination/hosts';
import { Kiosk } from '../../molecules/create-destination/kiosk';
import { Receptions } from '../../molecules/create-destination/receptions';
import { Translations } from '../../molecules/create-destination/translations';

import { useCreateDestinationValidationSchema } from './schema/create-destination-validation-schema';

import { Stepper } from '@/components/molecules/stepper/stepper';
import {
  createDestinationMachine,
  CreateDestinationStates,
  CreateDestinationTitle,
} from '@/features/destinations/utils/machine/create-destination-machine';
import { usePermissions } from '@/permissions/can';

export interface CreateDestinationFormValues {
  name: string;
  floor: string;
  description: string;
  maxCapacity: number;
  accessBeforeVisitInMinutes: number;
  accessAfterVisitInMinutes: number;

  translations: {
    pl?: string;
    en?: string;
    ua?: string;
  };
  directionTips: {
    pl: string;
    en: string;
    ua: string;
  }[];

  hosts: { id: number; name: string }[];
  devices: { id: number; name: string }[];

  allowedKiosks: { id: number; name: string }[];
  defaultHostId: number | null;
  addHostStepEnabled: boolean;
  hostObligatory: boolean;
  hostConfirmationObligatory: boolean;
  hostConfirmationInEmail: boolean;
  deviceConfirmation: boolean;
  kioskVisitHours: number;
}

const defaultValues: CreateDestinationFormValues = {
  name: '',
  floor: '',
  description: '',
  maxCapacity: 1,
  accessBeforeVisitInMinutes: 0,
  accessAfterVisitInMinutes: 0,

  translations: {
    pl: '',
    en: '',
    ua: '',
  },
  directionTips: [{ pl: '', en: '', ua: '' }],

  hosts: [],
  devices: [],

  allowedKiosks: [],
  defaultHostId: null,
  addHostStepEnabled: false,
  hostObligatory: false,
  hostConfirmationObligatory: false,
  hostConfirmationInEmail: false,
  deviceConfirmation: false,
  kioskVisitHours: 1,
};

export interface CreateDestinationFormProps {
  hasOrganizationKiosk: boolean;
}

export const CreateDestinationForm = ({ hasOrganizationKiosk }: CreateDestinationFormProps) => {
  const { t } = useTranslation(['createDestination']);
  const permissions = usePermissions();
  const cannotManageDestinationHosts = permissions.cannot('manageDestinations', 'Hosts');

  const validationSchema = useCreateDestinationValidationSchema();

  const methods = useForm<CreateDestinationFormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const [current, send] = useMachine(createDestinationMachine, {
    input: {
      step: '1',
      content: CreateDestinationTitle.INFO,
      hasOrganizationKiosk,
      skipHostsStep: cannotManageDestinationHosts,
    },
  });

  const { value, context } = current;

  const stepTitles = [
    CreateDestinationTitle.INFO,
    CreateDestinationTitle.TRANSLATIONS,
    CreateDestinationTitle.DIRECTION_TIPS,
    ...(!cannotManageDestinationHosts ? [CreateDestinationTitle.HOSTS] : []),
    CreateDestinationTitle.RECEPTIONS,
    ...(hasOrganizationKiosk ? [CreateDestinationTitle.KIOSK] : []),
    CreateDestinationTitle.CONFIRMATION,
  ];

  const steps = stepTitles.map((title, index) => ({
    step: String(index + 1),
    title: t(title),
  }));

  const renderContent = (): ReactNode => {
    switch (value) {
      case CreateDestinationStates.INFO:
        return <DestinationInfo context={context} send={send} />;
      case CreateDestinationStates.TRANSLATIONS:
        return <Translations context={context} send={send} />;
      case CreateDestinationStates.DIRECTION_TIPS:
        return <DirectionTips context={context} send={send} />;
      case CreateDestinationStates.HOSTS:
        return cannotManageDestinationHosts ? null : <Hosts context={context} send={send} />;
      case CreateDestinationStates.RECEPTIONS:
        return <Receptions context={context} send={send} />;
      case CreateDestinationStates.KIOSK:
        return hasOrganizationKiosk ? <Kiosk context={context} send={send} /> : null;
      case CreateDestinationStates.CONFIRMATION:
        return <Confirmation context={context} send={send} />;
    }
  };

  return (
    <Card variant="outlined">
      <FormProvider {...methods}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item md={3} xs={12}>
              <Stepper orientation="vertical" steps={steps} context={context} />
            </Grid>
            <Grid item md={9} xs={12}>
              {renderContent()}
            </Grid>
          </Grid>
        </CardContent>
      </FormProvider>
    </Card>
  );
};
